/* eslint-disable @typescript-eslint/no-explicit-any */

/** @jsxImportSource @emotion/react */
import { SerializedStyles, css } from "@emotion/react";
import Head from "next/head";
import { FunctionComponent, useEffect, useRef, useState } from "react";

import { BorderRadiuses } from "@/tokens/border";

interface GistProps {
    className?: SerializedStyles;
    HTML?: string;
    Stylesheet?: string;
    URL: string;
}

declare const window: any;

export const Gist: FunctionComponent<GistProps> = (props) => {
    const [gistParams, setGist] = useState<{
        HTML: string | undefined;
        Stylesheet: string | undefined;
    }>({
        HTML: props.HTML,
        Stylesheet: props.Stylesheet,
    });

    const scriptTagRef = useRef<Node | undefined>();

    /*
     * Request Gist information and update in case blog hasn't been
     * rebuilt in a while, or Gist has been updated since build
     */
    useEffect(() => {
        const { pathname } = new URL(props.URL);
        const gistId = pathname.split("/").at(-1);

        const currentGistCallback: string =
            "embed_gist_callback_" + gistId?.toString();

        const gistScriptSrc = `https://gist.github.com/${gistId}.json?callback=${currentGistCallback}`;

        window[currentGistCallback as any] = (gistResponse: {
            div: string;
            stylesheet: string;
        }) => {
            setGist({
                HTML: gistResponse.div,
                Stylesheet: gistResponse.stylesheet,
            });
            document.head.removeChild(scriptTagRef.current as Node);
        };

        const scriptTag = document.createElement("script");
        scriptTag.type = "text/javascript";
        scriptTag.src = gistScriptSrc;
        scriptTagRef.current = document.head.appendChild(scriptTag);
    }, [props.URL, scriptTagRef]);

    /**
     * Rendering
     */
    if (!gistParams) {
        return;
    }
    return (
        gistParams.HTML && (
            <>
                <Head>
                    <link
                        href={gistParams.Stylesheet}
                        rel="stylesheet"
                        type="text/css"
                    />
                </Head>

                {/**
                 * Normally would not use dangerouslySetInnerHTML,
                 * but html-react-parser was causing an issue
                 * with prerendering. HTML comes from a trusted
                 * source (Github) so it seems like an ok solution.
                 *  */}
                <div
                    css={css({
                        "& .gist-file": {
                            borderRadius: BorderRadiuses["borderMedium"],
                            overflow: "hidden",
                        },
                    })}
                    dangerouslySetInnerHTML={{ __html: gistParams.HTML }}
                />
            </>
        )
    );
};
